import { formatDateToDateString } from '@/helpers/LocaleHelper';
import i18n from '@/i18n';
import { ThemeColors } from '@/enums/global/ThemeColors';

/**
 * Logistics CSV/Data Parser Helper
 * Handles shared spreadsheet data and montage CSV parsing
 */

import {
    SpreadsheetData,
    SpreadsheetColumn,
    GenericRawRow,
    DeliveryListFormData,
    MontageListFormData,
    MontageItemUpdatePayload,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import Delivery from '@/modules/Logistics/api/models/Delivery';
import DeliveryItemModel from '@/modules/Logistics/api/models/DeliveryItem';
import MontageList from '@/modules/Logistics/api/models/MontageList';
import MontageListItemModel from '@/modules/Logistics/api/models/MontageListItem';
import { Moment } from 'moment';
import moment from 'moment';

/**
 * Figures out what delimiter the CSV is using by checking the first line
 */
export function detectDelimiter(rawText: string): string {
    const firstLine = rawText.split('\n')[0];

    const semicolonCount = countOccurrences(firstLine, ';');
    const commaCount = countOccurrences(firstLine, ',');
    const tabCount = countOccurrences(firstLine, '\t');

    // Return whichever delimiter appears most often
    if (semicolonCount >= commaCount && semicolonCount >= tabCount) {
        return ';';
    }
    if (commaCount >= tabCount) {
        return ',';
    }
    return '\t';
}

/**
 * Counts how many times a character appears in a string
 */
function countOccurrences(text: string, character: string): number {
    let count = 0;
    for (let i = 0; i < text.length; i++) {
        if (text[i] === character) {
            count++;
        }
    }
    return count;
}

/**
 * Splits a line by delimiter and trims whitespace from each value
 */
function splitAndTrim(line: string, delimiter: string): string[] {
    const parts = line.split(delimiter);
    const trimmedParts: string[] = [];

    for (const part of parts) {
        trimmedParts.push(part.trim());
    }

    return trimmedParts;
}

/**
 * Converts parsed delivery data into the format x-data-spreadsheet expects
 */
export function buildSpreadsheetData(deliveryRows: GenericRawRow[], columns: SpreadsheetColumn[]): SpreadsheetData {
    const spreadsheetRows: Record<number, { cells: Record<number, { text: string; style?: number }> }> = {};
    const columnWidths: Record<number, { width: number }> = {};

    // Build header row (row 0)
    const headerCells: Record<number, { text: string; style?: number }> = {};
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
        headerCells[colIndex] = {
            text: i18n.t(columns[colIndex].title) as string,
            style: 0, // Header style
        };
    }
    spreadsheetRows[0] = { cells: headerCells };

    // Build data rows
    for (let rowIndex = 0; rowIndex < deliveryRows.length; rowIndex++) {
        const deliveryRow = deliveryRows[rowIndex];
        const cells: Record<number, { text: string; style?: number }> = {};

        for (let colIndex = 0; colIndex < columns.length; colIndex++) {
            const column = columns[colIndex];
            const propertyName = column.key;
            const cellValue = deliveryRow[propertyName] || '';

            cells[colIndex] = {
                text: cellValue,
                style: 1, // Data style
            };
        }

        // Row index + 1 because row 0 is the header
        spreadsheetRows[rowIndex + 1] = { cells };
    }

    // Build column widths
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
        columnWidths[colIndex] = {
            width: columns[colIndex].width || 100,
        };
    }

    return {
        name: i18n.t('Deliveries') as string,
        freeze: 'A2', // Freeze header row
        styles: [
            // Style 0: Header
            {
                bgcolor: ThemeColors.PrimaryHover,
                color: '#ffffff',
                font: { bold: true, size: 11 },
                align: 'center',
            },
            // Style 1: Data cells
            {
                bgcolor: '#ffffff',
                font: { size: 10 },
                align: 'left',
            },
            // Style 2: Warning cells (unmatched addresses)
            {
                bgcolor: '#fff3cd',
                font: { size: 10 },
                align: 'left',
            },
        ],
        rows: spreadsheetRows,
        cols: columnWidths,
    };
}

/**
 * Normalizes all line endings in a string to '\n'. Handles Windows (\r\n), old Mac (\r), and Unix (\n).
 */
export function normalizeLineEndings(text: string): string {
    return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/**
 * Parses raw CSV text into a generic array of rows (each row is an object with column headers as keys),
 * regardless of predefined headers or mapping. Useful for ad-hoc CSV parsing.
 */
export function parseCSVTextGeneric(rawText: string): {
    success: boolean;
    data: GenericRawRow[];
    errors: string[];
    rowCount: number;
    columns?: SpreadsheetColumn[];
} {
    const errors: string[] = [];
    if (!rawText || rawText.trim().length === 0) {
        return {
            success: false,
            data: [],
            errors: ['No data provided'],
            rowCount: 0,
        };
    }

    const delimiter = detectDelimiter(rawText);
    // Normalize line endings
    const normalizedText = normalizeLineEndings(rawText.trim());
    const allLines = normalizedText.split('\n');
    const extractedColumns = allLines[0] ? splitAndTrim(allLines[0], delimiter) : [];

    if (allLines.length === 0) {
        return {
            success: false,
            data: [],
            errors: ['No rows found'],
            rowCount: 0,
        };
    }

    const rows: GenericRawRow[] = [];

    for (let lineIndex = 1; lineIndex < allLines.length; lineIndex++) {
        const line = allLines[lineIndex];
        const trimmed = line.trim();

        if (trimmed.length === 0) {
            continue;
        }

        const values = splitAndTrim(trimmed, delimiter);
        const row: GenericRawRow = {};

        // Map each value to its corresponding column header
        for (let colIndex = 0; colIndex < extractedColumns.length; colIndex++) {
            const key = extractedColumns[colIndex];
            row[key] = values[colIndex] || '';
        }

        rows.push(row);
    }

    return {
        success: rows.length > 0,
        data: rows,
        errors,
        rowCount: rows.length,
        columns: mapExtractedColumnsToSpreadsheetColumns(extractedColumns) as SpreadsheetColumn[],
    };
}

/**
 * Format columns for spreadsheet editor
 */
export function mapExtractedColumnsToSpreadsheetColumns(extractedColumns: string[]): SpreadsheetColumn[] {
    const columns: SpreadsheetColumn[] = [];

    for (let i = 0; i < extractedColumns.length; i++) {
        const headerText = extractedColumns[i];

        columns.push({
            title: headerText,
            key: headerText, // Use the original header text as the key for generic parsing
            width: 150,
            editable: true,
            type: 'text',
        });
    }

    return columns;
}

/**
 * Print view column configuration
 * Used for the final delivery list print step
 */
export const PRINT_COLUMNS: SpreadsheetColumn[] = [
    { title: 'Poz.', key: 'position', width: 40 },
    { title: 'Ime stranke, kraj, naslov', key: 'clientAddress', width: 280 },
    { title: 'Dodatne opombe', key: 'additionalNotes', width: 150 },
    { title: 'Kontaktna številka', key: 'contactNumber', width: 120 },
    { title: 'PLAČILO', key: 'payment', width: 80 },
    { title: 'Kosov', key: 'quantity', width: 60 },
];

/**
 * Builds DeliveryListFormData from a Delivery model instance
 * Maps delivery attributes to the form data format used in the print step
 */
export function buildDeliveryFormData(
    delivery: Delivery | null,
    existingFormData: DeliveryListFormData | null,
): DeliveryListFormData {
    if (!delivery) {
        return {
            deliveryDate: formatDateToDateString(existingFormData?.deliveryDate) || '',
            vehicle: '',
            driver: '',
            departureTime: '',
            arrivalTime: '',
            finalKilometers: '',
            isPrinted: null,
            overallComment: '',
        };
    }

    return {
        deliveryDate: formatDateToDateString(delivery.date || existingFormData?.deliveryDate) || '',
        vehicle: delivery.vehicle || '',
        driver: delivery.driver || '',
        departureTime: formatDateToDateString(delivery.departureTime) || '',
        arrivalTime: formatDateToDateString(delivery.arrivalTime) || '',
        finalKilometers: delivery.finalKilometers || '',
        isPrinted: delivery.isPrinted ?? null,
        overallComment: delivery.overallComment || '',
    };
}

/**
 * Builds print-ready spreadsheet data from a Delivery model
 * Uses delivery.items (DeliveryItem[]) to build rows
 * - Concatenates clientName + city + street for address column
 * - Maps pieces to quantity
 * - Creates print-specific column structure
 */
export function buildPrintSpreadsheetData(delivery: Delivery | null): SpreadsheetData {
    const printRows: Record<number, { cells: Record<number, { text: string; style?: number }> }> = {};
    const columnWidths: Record<number, { width: number }> = {};

    // Build header row (row 0)
    const headerCells: Record<number, { text: string; style?: number }> = {};
    for (let colIndex = 0; colIndex < PRINT_COLUMNS.length; colIndex++) {
        headerCells[colIndex] = {
            text: i18n.t(PRINT_COLUMNS[colIndex].title) as string,
            style: 0,
        };
    }
    printRows[0] = { cells: headerCells };

    // Get items from delivery, sorted by position
    const items: DeliveryItemModel[] = delivery?.items || [];
    const sortedItems = [...items].sort((a, b) => (a.position || 0) - (b.position || 0));

    // Build print data rows from delivery items
    sortedItems.forEach((item, index) => {
        const printRowIndex = index + 1;

        // Build concatenated clientName + city + street
        const clientAddress = [item.clientName, item.city, item.street].filter(Boolean).join(', ');

        // Map print column keys to their values from DeliveryItem
        const valueMap: Record<string, string> = {
            position: String(item.position || printRowIndex),
            clientAddress,
            additionalNotes: item.notes || '',
            contactNumber: item.contactPhone || '',
            payment: item.payment || '',
            quantity: String(item.pieces || 0),
        };

        // Build print row cells from PRINT_COLUMNS
        const cells: Record<number, { text: string; style?: number }> = {};
        PRINT_COLUMNS.forEach((col, colIndex) => {
            cells[colIndex] = {
                text: valueMap[col.key] || '',
                style: 1,
            };
        });

        printRows[printRowIndex] = { cells };
    });

    // Build column widths
    for (let colIndex = 0; colIndex < PRINT_COLUMNS.length; colIndex++) {
        columnWidths[colIndex] = {
            width: PRINT_COLUMNS[colIndex].width || 100,
        };
    }

    return {
        name: 'DeliveryPrint',
        freeze: 'A2',
        styles: [
            // Style 0: Header
            {
                bgcolor: '#f0f0f0',
                color: '#000000',
                font: { bold: true, size: 10 },
                align: 'center',
            },
            // Style 1: Data cells
            {
                bgcolor: '#ffffff',
                font: { size: 10 },
                align: 'left',
            },
        ],
        rows: printRows,
        cols: columnWidths,
    };
}

export function formatDateString(date: string | Moment): string {
    const outputFormat = 'YYYY-MM-DD HH:mm:ss';

    // Common input formats to try parsing
    const inputFormats = [
        'DD.MM.YYYY',
        'DD.MM.YYYY HH:mm',
        'DD.MM.YYYY HH:mm:ss',
        'YYYY-MM-DD',
        'YYYY-MM-DDTHH:mm',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss.SSSZ',
        moment.ISO_8601,
    ];

    if (!date) {
        return '';
    }

    if (typeof date === 'string') {
        // Try parsing with known formats first
        const parsed = moment(date, inputFormats, true);
        if (parsed.isValid()) {
            return parsed.format(outputFormat);
        }
        // Fallback to moment's default parsing
        const fallback = moment(date);
        return fallback.isValid() ? fallback.format(outputFormat) : '';
    }

    return date.isValid() ? date.format(outputFormat) : '';
}

export function inferDayFromDeliveryDate(dateStr: string): string {
    const date = moment(dateStr);
    return date.isValid() ? date.format('dddd') : '';
}

/**
 * Syncs spreadsheet data back to parsed data array
 * Rebuilds the data array from spreadsheet rows (handles added/deleted rows)
 *
 * @param spreadsheet - The SpreadsheetData object with current cell values
 * @param columnDefinitions - Column definitions to map cell indices to keys
 * @param _originalParsedData - The original parsed data (unused, kept for signature compatibility)
 * @returns New parsed data array built from spreadsheet
 */
export function syncSpreadsheetToParsedData(
    spreadsheet: SpreadsheetData | null,
    columnDefinitions: SpreadsheetColumn[],
): Array<Record<string, any>> {
    if (!spreadsheet?.rows || !columnDefinitions.length) {
        return [];
    }

    const rebuiltParsedData: Array<Record<string, any>> = [];

    // Get spreadsheet row numbers, skip header row (row 0)
    const dataRowNumbers = Object.keys(spreadsheet.rows)
        .map(Number)
        .filter((rowNumber) => rowNumber > 0)
        .sort((a, b) => a - b);

    for (const spreadsheetRowNumber of dataRowNumbers) {
        const spreadsheetRow = spreadsheet.rows[spreadsheetRowNumber];

        if (!spreadsheetRow?.cells) {
            continue;
        }

        // Check if row has any non-empty cell content (skip completely blank rows)
        const rowHasContent = Object.values(spreadsheetRow.cells).some((cell) => cell?.text?.trim());
        if (!rowHasContent) {
            continue;
        }

        // Build a parsed data record from this spreadsheet row's cells
        const parsedRowRecord: Record<string, any> = {};

        for (let columnIndex = 0; columnIndex < columnDefinitions.length; columnIndex++) {
            const columnDef = columnDefinitions[columnIndex];
            const cellAtColumn = spreadsheetRow.cells[columnIndex];

            if (!columnDef?.key) {
                continue;
            }

            parsedRowRecord[columnDef.key] = cellAtColumn?.text || '';
        }

        rebuiltParsedData.push(parsedRowRecord);
    }

    return rebuiltParsedData;
}

// ============================================================================
// MONTAGE HELPERS
// ============================================================================

/**
 * Print column configuration for montage list output
 */
export const MONTAGE_PRINT_COLUMNS: SpreadsheetColumn[] = [
    { title: 'Poz.', key: 'position', width: 40 },
    { title: 'Stranka, kraj', key: 'contactPlace', width: 220 },
    { title: 'Sistem', key: 'system', width: 160 },
    { title: 'Opis dela', key: 'workDescription', width: 220 },
    { title: 'Št. dok.', key: 'documentNumber', width: 120 },
    { title: 'Termin', key: 'plan', width: 160 },
];

/**
 * Format raw row record for the Montage create API.
 * Strips internal/UI-only keys.
 */
export function formatDataForMontageAPI(
    data: Array<Record<string, any>>,
    additionalKeysToStrip: string[] = [],
): Array<Record<string, any>> {
    return stripKeysGeneric(data, additionalKeysToStrip);
}

function stripKeysGeneric(data: Array<Record<string, any>>, keys: string[]): Array<Record<string, any>> {
    if (!keys.length) {
        return JSON.parse(JSON.stringify(data));
    }
    return JSON.parse(JSON.stringify(data)).map((row: Record<string, any>) => {
        keys.forEach((key) => delete row[key]);
        return row;
    });
}

/**
 * Builds MontageListFormData from a MontageList model instance
 */
export function buildMontageFormData(list: MontageList | null): MontageListFormData {
    if (!list) {
        return {
            date: '',
            vehicle: '',
            installer: '',
            otherInstallers: '',
            preparedBy: '',
            isPrinted: null,
        };
    }

    return {
        date: formatDateToDateString(list.date) || '',
        vehicle: list.vehicle || '',
        installer: list.installer || '',
        otherInstallers: list.otherInstallers || '',
        preparedBy: list.preparedBy || '',
        isPrinted: list.isPrinted ?? null,
    };
}

/**
 * Build print-ready spreadsheet data from a MontageList model
 */
export function buildMontagePrintSpreadsheetData(list: MontageList | null): SpreadsheetData {
    const printRows: Record<number, { cells: Record<number, { text: string; style?: number }> }> = {};
    const columnWidths: Record<number, { width: number }> = {};

    const headerCells: Record<number, { text: string; style?: number }> = {};
    for (let colIndex = 0; colIndex < MONTAGE_PRINT_COLUMNS.length; colIndex++) {
        headerCells[colIndex] = {
            text: i18n.t(MONTAGE_PRINT_COLUMNS[colIndex].title) as string,
            style: 0,
        };
    }
    printRows[0] = { cells: headerCells };

    const items: MontageListItemModel[] = list?.items || [];
    const sortedItems = [...items].sort((a, b) => (a.position || 0) - (b.position || 0));

    sortedItems.forEach((item, index) => {
        const printRowIndex = index + 1;

        const contactPlace = [item.contact, item.place].filter(Boolean).join(', ');

        const plan = formatPlanRange(item.plannedStart, item.plannedEnd);

        const valueMap: Record<string, string> = {
            position: String(item.position || printRowIndex),
            contactPlace,
            system: item.system || '',
            workDescription: item.workDescription || '',
            documentNumber: item.documentNumber || '',
            plan,
        };

        const cells: Record<number, { text: string; style?: number }> = {};
        MONTAGE_PRINT_COLUMNS.forEach((col, colIndex) => {
            cells[colIndex] = {
                text: valueMap[col.key] || '',
                style: 1,
            };
        });

        printRows[printRowIndex] = { cells };
    });

    for (let colIndex = 0; colIndex < MONTAGE_PRINT_COLUMNS.length; colIndex++) {
        columnWidths[colIndex] = {
            width: MONTAGE_PRINT_COLUMNS[colIndex].width || 100,
        };
    }

    return {
        name: 'MontagePrint',
        freeze: 'A2',
        styles: [
            {
                bgcolor: '#f0f0f0',
                color: '#000000',
                font: { bold: true, size: 10 },
                align: 'center',
            },
            {
                bgcolor: '#ffffff',
                font: { size: 10 },
                align: 'left',
            },
        ],
        rows: printRows,
        cols: columnWidths,
    };
}

function formatPlanRange(start: string | null | undefined, end: string | null | undefined): string {
    if (!start && !end) {
        return '';
    }
    const fmt = (val: string | null | undefined) => {
        if (!val) {
            return '';
        }
        const m = moment(val);
        if (!m.isValid()) {
            return String(val);
        }
        return m.format('DD.MM. HH:mm');
    };
    const s = fmt(start);
    const e = fmt(end);
    if (s && e) {
        return `${s} - ${e}`;
    }
    return s || e;
}

/**
 * Extract rows from print spreadsheet (montage flow) and map back to item update payloads.
 * Print spreadsheet shows derived/aggregated columns, so the data array passed when updating
 * mirrors what the user can edit in the print view; the original parsed rows still carry the
 * untouched fields (system, contact, place, work description...).
 *
 * Because montage edit-mode reuses the print spreadsheet for editing visible cells,
 * we re-merge the print row values back onto the original parsed data using the position.
 */
export function extractMontagePrintRowsAsItemPayloads(
    spreadsheet: SpreadsheetData | null,
    existingItems: MontageListItemModel[],
): MontageItemUpdatePayload[] {
    if (!spreadsheet?.rows) {
        return [];
    }

    const payloads: MontageItemUpdatePayload[] = [];
    const rowIndices = Object.keys(spreadsheet.rows)
        .map(Number)
        .filter((idx) => idx > 0)
        .sort((a, b) => a - b);

    for (const rowIdx of rowIndices) {
        const row = spreadsheet.rows[rowIdx];
        if (!row?.cells) {
            continue;
        }

        const position = parseInt(row.cells[0]?.text || '0', 10);
        const contactPlace = row.cells[1]?.text || '';
        const system = row.cells[2]?.text || '';
        const workDescription = row.cells[3]?.text || '';
        const documentNumber = row.cells[4]?.text || '';

        // Parse "Contact, Place"
        const parts = contactPlace.split(',').map((s) => s.trim());
        const contact = parts[0] || '';
        const place = parts.slice(1).join(', ') || '';

        const original = existingItems.find((it) => it.position === position);

        payloads.push({
            position,
            contact: contact || null,
            place: place || null,
            system: system || null,
            workDescription: workDescription || null,
            documentNumber: documentNumber || null,
            // Preserve fields that aren't editable from the print view
            projectConnection: original?.projectConnection ?? null,
            user: original?.user ?? null,
            projectDocument: original?.projectDocument ?? null,
            plannedStart: original?.plannedStart ?? null,
            plannedEnd: original?.plannedEnd ?? null,
            inProduction: original?.inProduction ?? null,
            manufactured: original?.manufactured ?? null,
            productionStatus: original?.productionStatus ?? null,
        });
    }

    return payloads;
}
